import { Link } from "@tanstack/react-router";
import { CalendarCheck, ArrowRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";

interface ConsultCtaProps {
  /**
   * "inline" (default) – small pill-shaped button, fits into hero/CTA rows.
   * "section"          – full-width banner block for the end of content pages.
   * "compact"          – single-line label + arrow, for footers / dense areas.
   */
  variant?: "inline" | "section" | "compact";
  className?: string;
}

/**
 * Site-wide call-to-action linking to the /kontakt page.
 * Copy is bilingual (DE / EN) via the i18n context; the site default stays English.
 */
export function ConsultCta({ variant = "inline", className = "" }: ConsultCtaProps) {
  const { lang } = useI18n();
  const de = lang === "de";
  const label = de ? "Jetzt beraten lassen" : "Get a consultation now";
  const sub = de
    ? "Wir bieten diese Behandlung an – kostenloses Erstgespräch."
    : "We offer this treatment – free initial consultation.";
  const heading = de ? "Persönliche Beratung anfragen" : "Request a personal consultation";

  if (variant === "section") {
    return (
      <section
        aria-labelledby="consult-cta-heading"
        className={
          "mt-10 overflow-hidden rounded-2xl border border-indigo/30 bg-gradient-to-br from-indigo/10 via-background to-emerald/10 p-6 md:p-8 " +
          className
        }
      >
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-indigo/30 bg-indigo/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-indigo">
              <CalendarCheck aria-hidden="true" className="h-3.5 w-3.5" />
              {de ? "Behandlung verfügbar" : "Treatment available"}
            </p>
            <h2
              id="consult-cta-heading"
              className="mt-3 font-display text-2xl text-foreground md:text-3xl"
            >
              {heading}
            </h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">{sub}</p>
          </div>
          <Link
            to="/kontakt"
            className="group inline-flex flex-none items-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {label}
            <ArrowRight
              aria-hidden="true"
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
            />
          </Link>
        </div>
      </section>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to="/kontakt"
        className={
          "inline-flex items-center gap-1.5 text-sm font-semibold text-indigo underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
          className
        }
      >
        <CalendarCheck aria-hidden="true" className="h-4 w-4" />
        {label}
        <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
      </Link>
    );
  }

  return (
    <Link
      to="/kontakt"
      className={
        "group inline-flex items-center gap-2 rounded-full bg-indigo px-5 py-3 text-sm font-semibold text-indigo-foreground shadow-sm transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
        className
      }
    >
      <CalendarCheck aria-hidden="true" className="h-4 w-4" />
      {label}
      <ArrowRight
        aria-hidden="true"
        className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  );
}
